// Mark as ordered — the biggest single thing in the Purchasing Queue at 949
// lines, and the last piece extracted at Session 79.14c. It is where an approved
// request becomes a real order: vendor, per-item pricing, pack quantities, the
// PO number, the vendor's own reference, an expected arrival, and how the order
// actually reaches the vendor.
//
// #287 OPTION A LIVES HERE AND SURPRISES PEOPLE: the PO number is assigned when
// this sheet OPENS, not when it is confirmed, because purchasing reads it to the
// vendor while placing the order — a number that only appears after the call is
// a number nobody could give out. The accepted cost is that opening this sheet
// and cancelling BURNS one, so gaps in the PO sequence are ordinary. Session
// 79.14c's own before-and-after capture burned two and they are the only
// difference between the two runs.
//
// #545 ALSO LIVES HERE, AS AN ABSENCE ELSEWHERE: this sheet is the ONLY place in
// the whole application that displays the PO number Stede issued. It closes when
// the order is confirmed and the number is never shown again.
//
// needsSpendApproval is imported rather than inlined — it decides whether an
// order needs an executive's sign-off and it is tested in lib/purchasingQueue.

import React, { useState, useEffect, useRef } from 'react';
import { IconCheck, IconChevronDown, IconCopy, IconPlus, IconSearch, IconStar } from '@tabler/icons-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../AuthContext';
import { canAddVendor } from '../../lib/permissions';
import { purchaseOrderBlockers } from '../../lib/purchaseOrder';
import { BRANCHES, needsSpendApproval } from '../../lib/purchasingQueue';
import { itemLabel, itemUom, preferredVendorPicks } from '../../lib/requestItems';
import UnitSelect from '../UnitSelect';
import Overlay from '../Overlay';
import VendorContactCard from '../inventory/VendorContactCard';
import SendOrderSection from './SendOrderSection';
import AddVendorSheet from './AddVendorSheet';
import SendAsTransferPanel from './SendAsTransferPanel';
import { sendRequestAsTransfer } from '../../lib/branchTransfers';
import { useTier, controlHeight } from '../../lib/tiers';

export default function MarkOrderedSheet({
  visible, req, isMobile,
  // #614 (gate 371 D38, approved Sep 21): the internal-vendor signpost no
  // longer navigates, so `setActive` is gone from here - it was #371 D22's
  // way out, built as a link to Transfers that carried nothing and threw the
  // order away. The page is told instead when a request has gone as a
  // transfer, so it can reload its queue.
  onSentAsTransfer,
  moVendor, setMoVendor,
  moVendorSearch, setMoVendorSearch,
  moVendorResults, setMoVendorResults,
  moPrice, setMoPrice,
  moPackQty, setMoPackQty,
  moPackUnit, setMoPackUnit,
  moPacksToOrder, setMoPacksToOrder,
  moPoRef, setMoPoRef,
  moPoGenerated, moPoIssue, setMoPoIssue,
  moVendorOrderRef, setMoVendorOrderRef,
  moEta, setMoEta,
  moVendorItemNum, setMoVendorItemNum,
  moItemState, toggleMoItemChecked, setMoItemPrice, setMoItemPacks,
  moSaving, moError, moDone, moPriceWarning,
  settings,
  branchRows,
  moSendMode, setMoSendMode,
  moVendorNote, setMoVendorNote,
  moSendPo, setMoSendPo,
  moSendConfirmation, setMoSendConfirmation,
  moSendResult,
  onSubmit, onClose, canContact,
}) {
  const { profile, isAdmin, isManager, isExecutive, isPurchasing } = useAuth();
  const canAdd = canAddVendor({ isAdmin, isManager, isExecutive, isPurchasing, profile });
  const [showAddVendor, setShowAddVendor] = useState(false);
  const [addVendorPrefill, setAddVendorPrefill] = useState('');
  const vendorSearchTimer = useRef(null);
  const [vendorDropdownOpen, setVendorDropdownOpen] = useState(false);
  const [vendorBrowse, setVendorBrowse] = useState(false);
  // #614 -- sending the request to one of our own branches as a transfer.
  const [sendingTo, setSendingTo] = useState(null);   // the internal vendor chosen
  const [sendBusy, setSendBusy] = useState(false);
  const [sendError, setSendError] = useState('');
  const [sent, setSent] = useState(null);             // what the database reported
  const { coarse } = useTier();
  // Another request, or the sheet closing, starts clean.
  useEffect(() => {
    setSendingTo(null); setSent(null); setSendError(''); setSendBusy(false);
  }, [req?.id, visible]);

  // Opening the list from the chevron is a different act from typing into the box, and
  // SAP Fiori draws exactly this line: clicking the field puts a cursor in it, clicking
  // the arrow opens the option list. Browsing must not disturb the current selection --
  // moVendor is left alone, so dismissing without choosing loses nothing.
  function toggleVendorList() {
    if (vendorDropdownOpen) {
      setVendorDropdownOpen(false);
      setVendorBrowse(false);
      return;
    }
    setVendorBrowse(true);
    setVendorDropdownOpen(true);
    loadAllVendors();
  }

  // #271. The search input nulls moVendor on every keystroke so the dropdown can reopen,
  // which means moVendor is already null by the time selectVendor() runs and cannot tell
  // us which vendor the buyer started from. This ref survives that, so "did the vendor
  // actually change" stays answerable. Reset when the sheet closes, or a second request
  // opened afterwards would compare against the previous one's vendor.
  const lastVendorIdRef = useRef(null);

  useEffect(() => {
    if (moVendor) lastVendorIdRef.current = moVendor.id;
  }, [moVendor]);

  useEffect(() => {
    if (!visible) { setShowAddVendor(false); lastVendorIdRef.current = null; }
  }, [visible]);

  async function loadAllVendors() {
    const { data } = await supabase
      .from('inventory_vendors')
      .select('id, name, contact_name, email, phone, payment_terms, is_internal, branch_id')
      .eq('is_active', true)
      .order('name', { ascending: true })
      .limit(50);
    setMoVendorResults(data || []);
  }

  useEffect(() => {
    // Browse mode is the chevron's doing: the full vendor list is already loaded and the
    // search box still holds the selected vendor's name, so letting the search below run
    // would replace that list with the one vendor whose name is in the box. Bail out and
    // leave what loadAllVendors() put there.
    if (vendorBrowse) return;
    if (moVendor) {
      setMoVendorResults([]);
      return;
    }
    if (!moVendorSearch.trim()) {
      if (vendorDropdownOpen) loadAllVendors();
      else setMoVendorResults([]);
      return;
    }
    clearTimeout(vendorSearchTimer.current);
    vendorSearchTimer.current = setTimeout(async () => {
      const { data } = await supabase
        .from('inventory_vendors')
        .select('id, name, contact_name, email, phone, payment_terms, is_internal, branch_id')
        .eq('is_active', true)
        .ilike('name', `%${moVendorSearch}%`)
        .limit(20);
      setMoVendorResults(data || []);
    }, 300);
  }, [moVendorSearch, moVendor, vendorDropdownOpen, vendorBrowse, setMoVendorResults]);

  if (!visible || !req) return null;

  const items = req.material_request_items || [];
  const item = items[0];

  // #614 -- the branch an internal vendor stands for, by name.
  const branchNameFor = v => (v?.branch_id != null
    ? (BRANCHES.find(b => String(b.id) === String(v.branch_id))?.name || v.name)
    : v?.name);
  const askingName = BRANCHES.find(b => String(b.id) === String(req.branch_id))?.name || 'The branch';
  async function sendAsTransfer(sendableIds) {
    setSendBusy(true);
    setSendError('');
    const branchNames = Object.fromEntries(BRANCHES.map(b => [String(b.id), b.name]));
    const res = await sendRequestAsTransfer({
      requestId: req.id,
      fromBranchId: sendingTo.branch_id,
      requestItemIds: sendableIds,
      requesterName: req.users?.full_name || null,
      branchNames,
    });
    setSendBusy(false);
    if (res.error) {
      setSendError(res.error.message || 'That did not send. Nothing changed \u2014 try again.');
      return;
    }
    setSent({ ...res.result, supplierName: branchNameFor(sendingTo) });
    onSentAsTransfer?.(res.result);
  }
  const isOffCatalog = item?.is_off_catalog;
  const itemName = isOffCatalog ? (item.off_catalog_description || '—') : (item?.inventory_items?.name || '—');
  const itemId = item?.inventory_items?.id || null;
  const checkedItems = items.filter(it => moItemState[it.id]?.checked);
  const checkedCount = checkedItems.length;
  const isSplitPreview = !isOffCatalog && checkedCount > 0 && checkedCount < items.length;

  // Issue #227 - the quick-pick, the Preferred badge, and the "other vendors" filter all
  // derive from every currently-checked item rather than material_request_items[0]. A
  // multi-vendor batch now offers each distinct preferred vendor as its own quick-pick,
  // labelled with how many of the checked items it actually covers.
  const preferredPicks = isOffCatalog ? [] : preferredVendorPicks(checkedItems);
  const selectedPick = moVendor ? preferredPicks.find(p => p.vendor.id === moVendor.id) || null : null;

  const packQtyNum = parseFloat(moPackQty) || 0;
  const priceNum = parseFloat(moPrice) || 0;
  // A price with no sells-by unit cannot be saved: the two foreign keys added at
  // 78n2 will not take an empty string, and more to the point the price is
  // meaningless without saying what it is per.
  const moUnitMissing = isOffCatalog && priceNum > 0 && !moPackUnit;
  const unitCost = packQtyNum > 0 ? priceNum / packQtyNum : 0;
  const packsToOrderNum = parseFloat(moPacksToOrder) || 0;
  const orderTotal = priceNum * packsToOrderNum;

  // #256. The sheet needs its own live total so the buyer can see, while typing, whether
  // this order still sits inside what was approved. The off-catalog shape already had
  // orderTotal above; the multi-item shape sums the checked rows the same way
  // submitMarkOrdered() does.
  const liveOrderTotal = isOffCatalog
    ? orderTotal
    : checkedItems.reduce((sum, it) => {
        const st = moItemState[it.id];
        return sum + (parseFloat(st?.price) || 0) * (parseFloat(st?.packs) || 0);
      }, 0);
  // #395. The order's lines, in src/lib/purchaseOrder.js's own shape. Mapped
  // HERE rather than inside that module, deliberately: it stays a pure function
  // over plain data, so every case in purchaseOrder.test.js can build an order
  // without a database, a request row or a React tree.
  //
  // The quantities and prices are read from exactly the same state liveOrderTotal
  // sums, so the total on the document cannot disagree with the total on the
  // sheet the buyer just read.
  const poLines = isOffCatalog
    ? [{
        name: item?.off_catalog_description || itemName,
        sku: '',
        qty: parseFloat(moPacksToOrder) || 0,
        unit: moPackUnit || item?.off_catalog_uom || '',
        pricePerPack: parseFloat(moPrice) || 0,
      }]
    : checkedItems.map(it => {
        const st = moItemState[it.id];
        return {
          name: it.inventory_items?.name || itemLabel(it),
          sku: it.inventory_items?.sku || '',
          qty: parseFloat(st?.packs) || 0,
          unit: itemUom(it) || '',
          pricePerPack: parseFloat(st?.price) || 0,
        };
      });

  // The branch ROW, which carries the address, not the BRANCHES constant, which
  // carries only an id and a name. See the branchRows loader for why both exist.
  const poBranch = (branchRows || []).find(b => b.id === req.branch_id) || null;

  // Computed HERE rather than inside SendOrderSection, since 78n8.2's live test.
  // The Confirm button below reads it too: with the send blocked, that button
  // still said "Mark ordered & send" and promised something it could not do.
  // One computation, so the button and the panel cannot disagree.
  const poBlockers = purchaseOrderBlockers({ vendor: moVendor, branch: poBranch, settings, buyer: profile });
  const poBlocked = poBlockers.length > 0;
  // The mode that will ACTUALLY run. A blocked send is a "don't send" whatever
  // the pills happen to show, and the submit handler derives the same value.
  const effectiveSendMode = poBlocked && moSendMode === 'send' ? 'none' : moSendMode;

  const approvedAmount = req.approved_amount != null ? parseFloat(req.approved_amount) : null;
  const wasApproved = req.approved_at != null && approvedAmount != null;
  const hasRestoredValues = isOffCatalog
    ? (req.ordered_price != null)
    : items.some(it => it.price_per_pack != null || it.packs_ordered != null);
  const willReTriggerApproval = needsSpendApproval(settings, liveOrderTotal, req.approved_amount);
  const overApprovedBy = wasApproved ? liveOrderTotal - approvedAmount : 0;

  // #371 and #425, Session 79.6, gate decision D22.
  //
  // WHAT THIS PREVENTS, and it is the whole of #425. Houston is an internal
  // vendor — it is a D&R branch, not a supplier. A stakes order placed against
  // it travels the entire purchasing path and is stopped at the very end,
  // because D&R does not send itself a bill. Every financial exit then needs its
  // own blocker, and the one somebody forgets is the one that sends D&R an
  // invoice from D&R.
  //
  // So an internal vendor is NOT SELECTABLE here. It is still shown, greyed,
  // saying what it is and where to go instead — because #425's whole diagnosis
  // is that people take the purchase path since it is the one they know, and a
  // vendor that silently vanishes from search teaches nothing.
  //
  // NOT done by switching Houston off in the catalog, which is the obvious move
  // and wrong four ways: it works on three of the four pickers, does nothing
  // about the fourth, breaks again the next time an internal vendor is created,
  // and tells a lie — "inactive" reads as *we stopped using this supplier*.
  //
  // CLEARED at approval: Colby confirmed Houston is no longer production's only
  // vendor, so filtering internal vendors out still leaves real suppliers
  // selectable. Acceptance line T20 keeps checking that as a regression guard.
  const selectableResults = moVendorResults.filter(v => !v.is_internal);
  const internalMatches = moVendorResults.filter(v => v.is_internal);
  const otherResults = selectableResults.filter(v => !preferredPicks.some(p => p.vendor.id === v.id));
  // The `!moVendor` term that used to sit here is deliberately gone. It made the list
  // unopenable once a vendor was chosen, which is what stranded the buyer: the popup was
  // still reachable by typing, but nothing on screen said so. W3C's combobox pattern is
  // explicit that a user must be able to explore the choices without losing the one
  // already made, and SAP Fiori, Carbon and Helios all keep the disclosure control
  // present after selection rather than swapping it for a state icon.
  // #371 D22 — the signpost counts as something to show. Without this term, a
  // buyer who types "Houston" gets an empty dropdown, which reads as "no such
  // vendor" and sends them to Add as new vendor. That would create a SECOND
  // Houston, external this time, and the blocker would never fire on it.
  const showDropdown = vendorDropdownOpen
    && (selectableResults.length > 0 || preferredPicks.length > 0 || internalMatches.length > 0);

  // #271. Clearing the off-catalog price when the vendor changes is correct -- a price
  // belongs to a vendor, per #18's canonical pricing model. It used to happen in the
  // search input's onChange, which fired it on every keystroke and threw away a price
  // the buyer had typed before ever reaching the vendor field. It now fires here, and
  // only when the vendor actually changed, so typing a few letters and settling on the
  // vendor already selected leaves the price alone.
  function selectVendor(v) {
    // Only a real vendor-to-different-vendor move clears the price. Picking the first
    // vendor does not: if nothing was selected, a price the buyer typed belongs to
    // nobody yet, and wiping it is the exact complaint #271 was raised about.
    const vendorChanged = lastVendorIdRef.current != null && lastVendorIdRef.current !== v.id;
    lastVendorIdRef.current = v.id;
    setMoVendor(v);
    setMoVendorSearch(v.name);
    setMoVendorResults([]);
    setVendorDropdownOpen(false);
    setVendorBrowse(false);
    setShowAddVendor(false);
    if (vendorChanged) setMoPrice('');
  }

  function handleAddVendorSaved(newVendor) {
    selectVendor(newVendor);
    setShowAddVendor(false);
  }

  // #344, Session 78d. Raised by Colby from a live desktop screenshot: this sheet
  // — Mark as ordered, the action Purchasing performs most — was the only one
  // that did not present as a centred modal on desktop. It rendered as a
  // bottom-right-anchored 460px drawer, rounded on its left edge only.
  //
  // The convention was not invented here, it was read out of this very file:
  // the two sheets further down both use `alignItems: isMobile ? 'flex-end' :
  // 'center'` with `justifyContent: 'center'`. This one was the outlier.
  //
  // Mobile is deliberately untouched — bottom sheet, full width, square top
  // corners rounded. It is the priority surface and it was already correct.
  //
  // #250 / #148 Split C, Session 79.14. Converted to Overlay.js - locked rule 3.
  //
  // This was the LAST of the six instances in this file and the one the previous
  // session explicitly declined to touch, on the grounds that an Overlay
  // migration of a 2,800-line file is not something to start at the tail of an
  // evening. That was the right call then. It is done now with the whole file
  // open and with the conversion verified on screen rather than by compiling.
  //
  // panelStyle keeps this sheet's own chrome exactly: the 92vh cap and the
  // column layout, because the black header stays put while the body scrolls;
  // and the shadow, which differs between the phone sheet rising off the bottom
  // edge and the desktop card floating in the middle. maxWidth={460} on the
  // modal variant is what minWidth/maxWidth/width were all saying together.
  //
  // The backdrop closed this unconditionally before, mid-typing included, so
  // Overlay's Escape key adds no loss that a stray click could not already
  // cause. It is NOT given a save guard, because it never had one.
  return (
    <Overlay
      variant={isMobile ? 'sheet' : 'modal'}
      maxWidth={460}
      zIndex={4000}
      onClose={onClose}
      panelStyle={{
        maxHeight: '92vh',
        display: 'flex', flexDirection: 'column',
        // Longhand since #554 - see ApprovalSheet.
        overflowY: 'hidden', overflowX: 'hidden',
        boxShadow: isMobile ? '0 -4px 32px rgba(0,0,0,0.14)' : '0 8px 40px rgba(0,0,0,0.18)',
        // 16px, not Overlay's default 14px. Measured after converting and it had
        // changed. Kept at the old value deliberately: corner radius is a TOKEN
        // and #417's design language gate decides tokens. Settling one inside a
        // plumbing refactor is the same mistake as quietly re-siting #505's
        // TrackingSheet while converting it.
        borderRadius: isMobile ? '16px 16px 0 0' : '16px',
      }}
    >
      <>
        <div style={{ background: '#0F0F0F', padding: '14px 18px', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'white' }}>
              {sent ? `Sent to ${sent.supplierName}`
                : sendingTo ? `Send ${req.request_number} to ${branchNameFor(sendingTo)}`
                : 'Mark as ordered'}
            </div>
            {isOffCatalog && (
              <span style={{ fontSize: 10, fontWeight: 700, color: '#EF9F27', background: 'rgba(239,159,39,0.15)', border: '1px solid rgba(239,159,39,0.4)', borderRadius: 4, padding: '1px 6px' }}>
                Off-catalog
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, color: '#9A9A9A' }}>
            {isOffCatalog
              ? <>{req.request_number} · {itemName} · {item?.qty_requested} {item.off_catalog_uom || ''}</>
              : <>{req.request_number} · {items.length} item{items.length !== 1 ? 's' : ''}</>
            }
          </div>
        </div>

        {/* #614 -- gate 371 D38-D40: sent, or the panel, before the order form. */}
        {sent ? (
          <div style={{ padding: '20px 20px 32px' }}>
            <div style={{ background: '#E8F5E9', borderRadius: 12, padding: 20, border: '1px solid #C8E6C9', textAlign: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 28, marginBottom: 6 }}>✓</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#2E7D32', marginBottom: 4 }}>Sent to {sent.supplierName}</div>
              <div style={{ fontSize: 12.5, color: '#3A3A3A', lineHeight: 1.5 }}>
                {sent.endorsed
                  ? `${sent.request_number} is waiting on ${sent.supplierName} to confirm. Nothing is bought.`
                  : `${sent.request_number} is waiting on ${askingName}'s approval, then ${sent.supplierName} confirms.`}
                {sent.split ? ` The lines you did not tick stay in Needs action as ${sent.parent_request_number}.` : ''}
              </div>
            </div>
            <button className="btn btn-primary" onClick={onClose}
              style={{ width: '100%', justifyContent: 'center', minHeight: controlHeight(coarse, 'commit') }}>
              Done
            </button>
          </div>
        ) : sendingTo ? (
          <SendAsTransferPanel
            req={req}
            checkedIds={checkedItems.map(it => it.id)}
            supplierName={branchNameFor(sendingTo)}
            askingName={askingName}
            isMobile={isMobile}
            busy={sendBusy}
            error={sendError}
            onClearError={() => setSendError('')}
            onBack={() => { setSendingTo(null); setSendError(''); }}
            onSend={sendAsTransfer}
          />
        ) : moDone ? (
          <div style={{ padding: '20px 20px 40px' }}>
            <div style={{ background: '#E8F5E9', borderRadius: 12, padding: 20, border: '1px solid #C8E6C9', textAlign: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 28, marginBottom: 6 }}>✓</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#2E7D32', marginBottom: 4 }}>
                {willReTriggerApproval ? 'Sent for approval' : 'Order placed'}
              </div>
              <div style={{ fontSize: 12, color: '#5C5C5C' }}>
                {willReTriggerApproval
                  ? 'Nothing is ordered yet. An admin or executive has to clear the spend first.'
                  // "Purchasing email sent." was struck at Session 78n8.2. It
                  // was already a claim rather than a fact -- sendEmail
                  // suppresses every send outside production, so this line
                  // asserted a send that had not happened on localhost and on
                  // every Preview. The moment #395 added an HONEST account of
                  // the vendor send directly beneath it, the two contradicted
                  // each other on the same panel: "Purchasing email sent"
                  // above, "the order was NOT emailed" below. They describe
                  // different emails, and no reader could know that.
                  //
                  // The block below is now the single place this sheet says
                  // what left the building, and it only says it when it did.
                  : 'Request updated.'}
              </div>
            </div>

            {/* #395, and the LIVE TEST at 78n8.2 is what put it here rather than
                above the Confirm button, where it was first written and could
                never be seen: setMoDone(true) swaps this whole body, so the
                buyer got the generic "Purchasing email sent" and nothing about
                what actually happened to the vendor's order.

                That mattered more than a missing line. On localhost and on any
                Preview, sendEmail SUPPRESSES every send -- so the sheet was
                reporting a send that had not happened and could not happen,
                which is the exact class of false success this whole build was
                careful to avoid everywhere else.

                Never red, even on failure: the order IS placed, and a red panel
                over a successful order invites someone to do it again. */}
            {moSendResult && (
              <div style={{
                background: moSendResult.ok ? '#F1F8F4' : '#FFF3E0',
                border: `1px solid ${moSendResult.ok ? '#C8E6C9' : '#FFE0B2'}`,
                borderRadius: 12, padding: '12px 14px', marginBottom: 16,
              }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: moSendResult.ok ? '#2E7D32' : '#9A5F00', marginBottom: 4 }}>
                  {moSendResult.ok ? 'What was sent' : 'The order is placed, but the send did not complete'}
                </div>
                <div style={{ fontSize: 11.5, color: moSendResult.ok ? '#3D6B4A' : '#7A5B1E', lineHeight: 1.55 }}>
                  {moSendResult.message}
                </div>
                {moSendResult.mailtoHref && (
                  <a href={moSendResult.mailtoHref}
                     style={{ display: 'inline-block', marginTop: 8, fontSize: 12, fontWeight: 600, color: '#0B5E43' }}>
                    Open the draft
                  </a>
                )}
              </div>
            )}
            {/* #351 — the order succeeded; these are the vendor prices that did
                not save. Amber, not red: nothing here needs undoing, but the
                vendor catalog now disagrees with what was actually paid, and
                that number is what the next order quotes from. */}
            {moPriceWarning && (
              <div style={{ background: '#FFF3E0', border: '1px solid #FFE0B2', borderRadius: 12, padding: '12px 14px', marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#9A5F00', marginBottom: 4 }}>Vendor price not saved</div>
                <div style={{ fontSize: 11, color: '#7A5B1E', lineHeight: 1.5 }}>
                  The order went through, but the catalog price could not be updated for {moPriceWarning.join(', ')}.
                  Set it by hand in the vendor catalog, or the next order will quote the old price.
                </div>
              </div>
            )}
            <button onClick={onClose} style={{ width: '100%', padding: 14, borderRadius: 12, fontSize: 14, fontWeight: 700, border: 'none', background: '#1D9E75', color: 'white', cursor: 'pointer' }}>Done</button>
          </div>
        ) : (
          <div style={{ padding: '16px 18px 40px', overflowY: 'auto', flex: 1, position: 'relative' }}>
            {moError && <div style={{ background: '#FFEBEE', color: '#A32D2D', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>{moError}</div>}

            {/* MR# read-only row */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#F5F5F3', borderRadius: 8, padding: '8px 12px', marginBottom: 12 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase' }}>MR#</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#0F0F0F' }}>{req.request_number}</span>
            </div>

            {/* WO# read-only row (if present) */}
            {req.work_order_ref && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#F0FFF8', border: '1px solid #A8DFC9', borderRadius: 8, padding: '8px 12px', marginBottom: 12 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#085041', textTransform: 'uppercase' }}>WO#</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#085041' }}>{req.work_order_ref}</span>
              </div>
            )}

            {hasRestoredValues && (
              <div style={{ background: '#E8F5E9', border: '1px solid #A5D6A7', borderRadius: 8, padding: '8px 10px', marginBottom: 12 }}>
                <div style={{ fontSize: 12, color: '#1B5E20', lineHeight: 1.45 }}>
                  {wasApproved
                    ? `Restored from approval — the prices, packs and arrival date ${req.approver?.full_name || 'the approver'} approved${approvedAmount != null ? ` at $${approvedAmount.toFixed(2)}` : ''}.`
                    : 'Restored from the last time this order was submitted — prices, packs and arrival date.'}
                </div>
              </div>
            )}

            <div style={{ fontSize: 10, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>Vendor used</div>
            <div style={{ position: 'relative', marginBottom: 0 }}>
              <div
                style={{ display: 'flex', alignItems: 'center', border: `1px solid ${moVendor ? '#1D9E75' : vendorDropdownOpen ? '#0F0F0F' : '#E0E0E0'}`, borderRadius: 9, overflow: 'hidden', cursor: 'text' }}
                onClick={() => { if (!moVendor) { setVendorBrowse(false); setVendorDropdownOpen(true); } }}
              >
                <IconSearch size={14} color="#9A9A9A" style={{ marginLeft: 10, flexShrink: 0 }} />
                <input
                  type="text"
                  value={moVendorSearch}
                  onChange={e => { setMoVendorSearch(e.target.value); setMoVendor(null); setVendorBrowse(false); setVendorDropdownOpen(true); }}
                  onFocus={() => { if (!moVendor) { setVendorBrowse(false); setVendorDropdownOpen(true); } }}
                  placeholder="Search or select a vendor..."
                  style={{ flex: 1, height: 40, border: 'none', padding: '0 10px', color: '#0F0F0F', outline: 'none', background: 'transparent' }}
                />
                {/* The chevron used to be swapped for a green check once a vendor was
                    chosen. The check was decoration -- the field text and the green
                    border already say "selected" twice over -- and it occupied the one
                    slot the control belongs in, so the list became unopenable in
                    appearance while staying openable in fact. The chevron now stays,
                    which is what SAP Fiori, Carbon and Helios all do, and it is the
                    element W3C's combobox pattern describes as indicating that the
                    popup is available. No clear "x" alongside it on purpose: two
                    adjacent targets is the clutter Carbon's own issue #4992 raised,
                    and typing over the field already clears it. */}
                <IconChevronDown
                  size={16}
                  color={moVendor ? '#1D9E75' : '#9A9A9A'}
                  style={{ marginRight: 10, flexShrink: 0, cursor: 'pointer', transform: vendorDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }}
                  onClick={e => { e.stopPropagation(); toggleVendorList(); }}
                />
              </div>

              {!isOffCatalog && selectedPick && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 5, marginBottom: 4 }}>
                  <span style={{ fontSize: 10, background: '#E1F5EE', color: '#085041', borderRadius: 4, padding: '1px 7px', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                    <IconStar size={9} /> {selectedPick.count === checkedCount ? 'Preferred' : `Preferred for ${selectedPick.count} of ${checkedCount}`}
                  </span>
                  {/* 74p. This used to print selectedPick.row.vendor_item_number here - one
                      item's part number beside the vendor chip, presented as if it covered the
                      whole order. Same shape as #227 and #230. Part numbers are now per line,
                      resolved against the selected vendor, in the item rows below. */}
                </div>
              )}

              {showDropdown && !showAddVendor && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #E0E0E0', borderRadius: 9, boxShadow: '0 4px 20px rgba(0,0,0,0.12)', zIndex: 100, overflow: 'hidden', marginTop: 2 }}>
                  {!isOffCatalog && preferredPicks.length > 0 && (
                    <>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '.5px', padding: '6px 12px 4px', background: '#F9F9F7' }}>
                        {preferredPicks.length === 1 ? 'Preferred for the selected items' : 'Preferred vendors across the selected items'}
                      </div>
                      {preferredPicks.map(pick => (
                        <button
                          key={pick.vendor.id}
                          onClick={() => selectVendor(pick.vendor)}
                          style={{ width: '100%', padding: '9px 12px', border: 'none', background: moVendor?.id === pick.vendor.id ? '#E1F5EE' : '#F5FFF9', textAlign: 'left', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #F0F0F0' }}
                        >
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: '#1D9E75', display: 'flex', alignItems: 'center', gap: 5 }}>
                              {moVendor?.id === pick.vendor.id && <IconCheck size={13} color="#1D9E75" />}
                              {pick.vendor.name}
                            </div>
                            <div style={{ fontSize: 11, color: '#7A7A7A' }}>
                              {pick.count} of {checkedCount} selected item{checkedCount !== 1 ? 's' : ''}
                            </div>
                          </div>
                          <span style={{ fontSize: 10, background: '#E1F5EE', color: '#085041', borderRadius: 4, padding: '2px 7px', fontWeight: 700 }}>Preferred</span>
                        </button>
                      ))}
                    </>
                  )}

                  {otherResults.length > 0 && (
                    <>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '.5px', padding: '6px 12px 4px', background: '#F9F9F7' }}>
                        Other vendors
                      </div>
                      {otherResults.map(v => (
                        <button key={v.id} onClick={() => selectVendor(v)}
                          style={{ width: '100%', padding: '9px 12px', border: 'none', background: moVendor?.id === v.id ? '#E1F5EE' : 'white', textAlign: 'left', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid #F0F0F0', display: 'block' }}>
                          <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5 }}>
                            {moVendor?.id === v.id && <IconCheck size={13} color="#1D9E75" />}
                            {v.name}
                          </div>
                          {v.contact_name && <div style={{ fontSize: 11, color: '#7A7A7A' }}>{v.contact_name}</div>}
                        </button>
                      ))}
                    </>
                  )}

                  {/* #371 and #425, gate D22. Greyed, not hidden, and it names
                      itself — because #425's diagnosis is that people take the
                      purchase path since it is the one they know, and a vendor
                      that silently vanishes from search teaches nothing. */}
                  {/* #614, gate 371 D38 (approved by Colby, Sep 21): the button
                      SENDS the order to that branch as a transfer, inside this
                      sheet. It was "Go to Transfers", which threw the order away
                      and landed on an empty list. The text now reads at the
                      floor: the name 10.90 (was 2.70), the badge 5.71 (was 3.67),
                      the sentence 6.41 (was 4.11). */}
                  {internalMatches.map(v => {
                    const branch = branchNameFor(v);
                    const own = v.branch_id != null && String(v.branch_id) === String(req.branch_id);
                    return (
                      <div key={`internal-${v.id}`}
                        style={{ padding: '9px 12px', borderBottom: '1px solid #F0F0F0', background: '#FAFAFA' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: '#3A3A3A' }}>
                          {v.name}
                          <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', background: '#EDEDED', color: '#5C5C5C', borderRadius: 999, padding: '1px 6px' }}>
                            D&amp;R branch
                          </span>
                        </div>
                        <div style={{ fontSize: 11.5, color: '#5C5C5C', marginTop: 3, lineHeight: 1.4 }}>
                          {v.name} is one of our own branches — there is no purchase order and no invoice.
                        </div>
                        {/* X2: a press is answered, never silent (#492's rule). */}
                        {v.branch_id == null ? (
                          <div style={{ fontSize: 11.5, color: '#A32D2D', marginTop: 6, lineHeight: 1.4 }}>
                            {v.name} is not linked to a branch in Stede. Ask an admin to fix the vendor.
                          </div>
                        ) : own ? (
                          <div style={{ fontSize: 11.5, color: '#5C5C5C', marginTop: 6, lineHeight: 1.4 }}>
                            This is {branch}&rsquo;s own request — take it from {branch}&rsquo;s shelf.
                          </div>
                        ) : (
                          <button
                            onClick={() => { setSendError(''); setSendingTo(v); }}
                            className="btn btn-ghost btn-sm"
                            style={{ marginTop: 7, minHeight: controlHeight(coarse, 'min') }}
                          >
                            Send to {branch} as a transfer
                          </button>
                        )}
                      </div>
                    );
                  })}

                  {/* The quoted form only makes sense when the buyer typed a name that
                      found nothing. Browsing the full list from the chevron leaves the
                      selected vendor's name in the box, and a blank box used to render
                      Add "" as new vendor -- both offer to create a vendor nobody asked
                      for. Prefill follows the same rule so the AddVendorSheet does not
                      open pre-filled with an existing vendor's name. */}
                  {/* #371 and #425, Session 79.6. FOUND BY DRIVING THE SCREEN,
                      not by reading it: with the signpost in place, searching
                      "Houston" showed the greyed branch AND, directly beneath
                      it, `Add "Houston" as new vendor`.

                      That is worse than the problem D22 fixes. A buyer who
                      cannot select Houston is handed a button that CREATES A
                      SECOND HOUSTON, external this time — and every guard in
                      the money path keys on `is_internal`, so the QuickBooks
                      blocker would never fire on the duplicate. D&R would send
                      itself a bill through a vendor Stede believes is real.

                      So the offer is withdrawn when the typed name matches an
                      internal vendor already on file. Matched case-insensitively
                      and on the whole trimmed name, because "houston" and
                      "Houston " are the same intent. */}
                  {canAdd && !internalMatches.some(v =>
                    v.name.trim().toLowerCase() === moVendorSearch.trim().toLowerCase()
                  ) && (() => {
                    const typedName = vendorBrowse ? '' : moVendorSearch.trim();
                    return (
                      <button
                        onClick={() => { setAddVendorPrefill(typedName); setShowAddVendor(true); setMoVendorResults([]); }}
                        style={{ width: '100%', padding: '9px 12px', border: 'none', background: 'white', textAlign: 'left', cursor: 'pointer', fontSize: 13, color: '#1D9E75', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}
                      >
                        <IconPlus size={13} /> {typedName ? `Add "${typedName}" as new vendor` : 'Add a new vendor'}
                      </button>
                    );
                  })()}
                </div>
              )}
            </div>

            {canContact && (
              <div style={{ marginTop: !isOffCatalog && selectedPick ? 6 : 10, marginBottom: 14 }}>
                <VendorContactCard vendor={moVendor} />
              </div>
            )}

            {/* PRICING SECTION */}
            <div style={{ fontSize: 10, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>
              Pricing
            </div>

            {isOffCatalog ? (
              <>
                {/* Off-catalog pack price block */}
                <div style={{ background: '#F9F9F7', border: '1px solid #E8E8E8', borderRadius: 10, padding: '12px', marginBottom: 12 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 5 }}>Vendor sells by</div>
                      <UnitSelect
                        kind="package"
                        value={moPackUnit}
                        onChange={setMoPackUnit}
                        placeholder="pick one"
                        ariaLabel="What the vendor sells this by"
                        style={{ height: 38 }}
                      />
                      {/* The requester's own words, as CONTEXT rather than as an
                          answer. It used to be pre-filled into the control above,
                          which asserted that the vendor ships in whatever unit the
                          requester happens to count in. Showing it here keeps the
                          information and drops the assertion. */}
                      {item?.off_catalog_uom && (
                        <div style={{ fontSize: 10, color: '#9A9A9A', marginTop: 4, lineHeight: 1.4 }}>
                          Requested as {item.qty_requested} {item.off_catalog_uom}
                        </div>
                      )}
                    </div>
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 5 }}>Units per pack</div>
                      <input
                        type="number" min="1" value={moPackQty}
                        onChange={e => setMoPackQty(e.target.value)}
                        placeholder="e.g. 10"
                        style={{ width: '100%', height: 38, border: '1px solid #E0E0E0', borderRadius: 8, padding: '0 10px', color: '#0F0F0F', boxSizing: 'border-box' }}
                      />
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 5 }}>Price per pack</div>
                    <div style={{ position: 'relative' }}>
                      <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 14, color: '#5C5C5C' }}>$</span>
                      <input
                        type="number" step="0.01" value={moPrice}
                        onChange={e => setMoPrice(e.target.value)}
                        placeholder="0.00"
                        style={{ width: '100%', height: 38, border: '1px solid #E0E0E0', borderRadius: 8, padding: '0 10px 0 22px', color: '#0F0F0F', boxSizing: 'border-box' }}
                      />
                    </div>
                  </div>

                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 5 }}>Quantity to order</div>
                    <input
                      type="number" min="1" step="1" value={moPacksToOrder}
                      onChange={e => setMoPacksToOrder(e.target.value)}
                      placeholder="1"
                      style={{ width: '100%', height: 38, border: '1px solid #E0E0E0', borderRadius: 8, padding: '0 10px', color: '#0F0F0F', boxSizing: 'border-box' }}
                    />
                  </div>

                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 5 }}>
                      Vendor item # <span style={{ fontWeight: 400, textTransform: 'none' }}>(optional)</span>
                    </div>
                    <input
                      type="text" value={moVendorItemNum}
                      onChange={e => setMoVendorItemNum(e.target.value)}
                      placeholder="e.g. AB-38-SLV"
                      style={{ width: '100%', height: 38, border: '1px solid #E0E0E0', borderRadius: 8, padding: '0 10px', color: '#0F0F0F', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', boxSizing: 'border-box' }}
                    />
                    <div style={{ fontSize: 10, color: '#9A9A9A', marginTop: 3, lineHeight: 1.4 }}>
                      The vendor's own part number. Carried onto the catalog entry if this item is later promoted.
                    </div>
                  </div>

                  {/* Live calc row */}
                  {priceNum > 0 && packQtyNum > 0 && (
                    <div style={{ marginTop: 10, background: '#E8F5E9', borderRadius: 8, padding: '8px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ fontSize: 12, color: '#085041' }}>
                        Unit cost: <strong>${unitCost.toFixed(2)}</strong> / {item?.off_catalog_uom || 'unit'}
                      </div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#085041', background: '#C8E6C9', borderRadius: 6, padding: '2px 8px' }}>
                        Est. total: ${orderTotal.toFixed(2)}
                      </div>
                    </div>
                  )}

                  <div style={{ marginTop: 8, fontSize: 10, color: '#9A9A9A', lineHeight: 1.4 }}>
                    Unit cost = price per pack divided by units per pack. Saved to vendor record on confirm.
                  </div>
                </div>
              </>
            ) : (
              <>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>
                  Items to order
                </div>
                {items.map(it => {
                  const st = moItemState[it.id] || { checked: true, price: '', packs: '1' };
                  const itPreferred = it.inventory_items?.vendor_items?.find(vi => vi.is_preferred);
                  const itPreferredVendorName = itPreferred?.inventory_vendors?.name;
                  const itPriceNum = parseFloat(st.price) || 0;
                  const itPacksNum = parseFloat(st.packs) || 0;
                  const itLineTotal = itPriceNum * itPacksNum;
                  const itIsPriceLocked = itPreferred?.price_locked && moVendor?.id === itPreferred?.inventory_vendors?.id;
                  // 74p. Resolved against the vendor actually selected for this order, not
                  // against is_preferred. Getting that backwards would print one vendor's part
                  // number while buying from another - #16's failure mode, except what arrives
                  // is the wrong physical part.
                  const itSelectedVendorRow = moVendor
                    ? it.inventory_items?.vendor_items?.find(vi => vi.vendor_id === moVendor.id)
                    : null;
                  const itVendorItemNumber = itSelectedVendorRow?.vendor_item_number || null;
                  const itPreferredIsSelected = !!moVendor && itPreferred?.inventory_vendors?.id === moVendor.id;
                  return (
                    <div key={it.id} style={{ border: '1px solid #E8E8E8', borderRadius: 10, padding: '10px 12px', marginBottom: 8, background: st.checked ? 'white' : '#F9F9F7' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                        <button
                          type="button"
                          onClick={() => toggleMoItemChecked(it.id)}
                          style={{ width: 20, height: 20, borderRadius: 5, border: st.checked ? 'none' : '1px solid #C0C0C0', background: st.checked ? '#1D9E75' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, marginTop: 1, padding: 0 }}
                        >
                          {st.checked && <IconCheck size={13} color="white" />}
                        </button>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: '#0F0F0F' }}>{it.inventory_items?.name || '—'}</div>

                          {moVendor && (
                            itVendorItemNumber ? (
                              <button
                                type="button"
                                onClick={() => navigator.clipboard?.writeText(itVendorItemNumber)}
                                title="Copy item number"
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, border: '1px solid #D8D8D4', background: '#F7F7F5', borderRadius: 5, padding: '2px 6px', margin: '3px 0 2px', cursor: 'pointer' }}
                              >
                                {!isMobile && <span style={{ fontSize: 9, color: '#7A7A7A', letterSpacing: '.4px' }}>ITEM #</span>}
                                <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12, color: '#0F0F0F' }}>{itVendorItemNumber}</span>
                                <IconCopy size={12} color="#7A7A7A" />
                              </button>
                            ) : (
                              <div style={{ display: 'inline-flex', alignItems: 'center', border: '1px dashed #D8D8D4', borderRadius: 5, padding: '2px 6px', margin: '3px 0 2px' }}>
                                <span style={{ fontSize: 11, color: '#9A9A9A', fontStyle: 'italic' }}>No item # on file</span>
                              </div>
                            )
                          )}

                          <div style={{ fontSize: 11, color: '#7A7A7A' }}>{it.qty_requested} {it.inventory_items?.uom || ''} needed</div>
                          {itPreferredVendorName && (
                            moVendor && !itPreferredIsSelected ? (
                              <div style={{ fontSize: 11, color: '#854F0B', marginTop: 1 }}>
                                Preferred vendor is {itPreferredVendorName}
                              </div>
                            ) : (
                              <div style={{ fontSize: 11, color: '#1D9E75', fontWeight: 600, marginTop: 1 }}>
                                {itPreferredVendorName}
                                {itIsPriceLocked && (
                                  <span style={{ marginLeft: 6, fontSize: 9, background: '#EEEDFE', color: '#3C3489', padding: '1px 6px', borderRadius: 4, fontWeight: 700 }}>CONTRACT — LOCKED</span>
                                )}
                              </div>
                            )
                          )}
                          {st.checked ? (
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 }}>
                              <div>
                                <div style={{ fontSize: 9, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 3 }}>Price / pack</div>
                                <div style={{ position: 'relative' }}>
                                  <span style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: '#5C5C5C' }}>$</span>
                                  <input
                                    type="number" step="0.01" value={st.price}
                                    onChange={e => setMoItemPrice(it.id, e.target.value)}
                                    disabled={itIsPriceLocked}
                                    placeholder="0.00"
                                    style={{ width: '100%', height: 34, border: '1px solid #E0E0E0', borderRadius: 7, padding: '0 8px 0 18px', color: itIsPriceLocked ? '#9A9A9A' : '#0F0F0F', boxSizing: 'border-box', fontSize: 16, background: itIsPriceLocked ? '#F5F5F3' : 'white' }}
                                  />
                                </div>
                              </div>
                              <div>
                                <div style={{ fontSize: 9, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 3 }}>Packs to order</div>
                                <input
                                  type="number" min="1" step="1" value={st.packs}
                                  onChange={e => setMoItemPacks(it.id, e.target.value)}
                                  placeholder="1"
                                  style={{ width: '100%', height: 34, border: '1px solid #E0E0E0', borderRadius: 7, padding: '0 8px', color: '#0F0F0F', boxSizing: 'border-box', fontSize: 16 }}
                                />
                              </div>
                              {itPriceNum > 0 && itPacksNum > 0 && (
                                <div style={{ gridColumn: '1 / -1', fontSize: 11, color: '#085041', fontWeight: 600 }}>
                                  Line total: ${itLineTotal.toFixed(2)}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div style={{ fontSize: 11, color: '#9A9A9A', fontStyle: 'italic', marginTop: 4 }}>Not included in this order</div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </>
            )}

            <div style={{ fontSize: 10, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>
              PO number
            </div>
            {moPoGenerated ? (
              // Rendered as a chip rather than a disabled input on purpose. A greyed-out
              // text box invites people to try to type in it; a chip reads as issued.
              <button
                type="button"
                onClick={() => { if (navigator.clipboard) navigator.clipboard.writeText(moPoRef); }}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: 10, background: '#FAEEDA', border: '1px solid #EF9F27', borderRadius: 9,
                  padding: '9px 12px', cursor: 'pointer', textAlign: 'left', marginBottom: 4,
                }}
              >
                <span style={{ fontSize: 17, fontWeight: 700, color: '#633806', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', letterSpacing: '.04em' }}>
                  {moPoRef}
                </span>
                <span style={{ fontSize: 10, color: '#633806', opacity: .85, textAlign: 'right', lineHeight: 1.35 }}>
                  Assigned by Stede<br />Tap to copy
                </span>
              </button>
            ) : (
              <input
                type="text" value={moPoRef} onChange={e => { setMoPoRef(e.target.value); setMoPoIssue(''); }}
                placeholder={settings?.po_number_format || 'e.g. PO-1042'}
                style={{
                  width: '100%', height: 40,
                  border: `1px solid ${moPoIssue ? '#A32D2D' : '#E0E0E0'}`,
                  background: moPoIssue ? '#FBEDED' : 'white',
                  borderRadius: 9, padding: '0 12px', color: '#0F0F0F', marginBottom: 4,
                  boxSizing: 'border-box', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                }}
              />
            )}
            {moPoIssue ? (
              <div style={{ fontSize: 11.5, color: '#A32D2D', marginBottom: 12, lineHeight: 1.45 }}>{moPoIssue}</div>
            ) : (
              <div style={{ fontSize: 11.5, color: '#9A9A9A', marginBottom: 12, lineHeight: 1.45 }}>
                {moPoGenerated
                  ? 'Give this number to the vendor. It is how the invoice will be matched back to this order.'
                  : settings?.po_number_format_enforced && settings?.po_number_format
                    ? `Needs to look like ${settings.po_number_format}.`
                    : 'No two orders can carry the same PO number.'}
              </div>
            )}

            <div style={{ fontSize: 10, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>
              Vendor&apos;s order # <span style={{ fontWeight: 400, textTransform: 'none' }}>(optional)</span>
            </div>
            <input
              type="text" value={moVendorOrderRef} onChange={e => setMoVendorOrderRef(e.target.value)}
              placeholder="e.g. SO-88421"
              style={{ width: '100%', height: 40, border: '1px solid #E0E0E0', borderRadius: 9, padding: '0 12px', color: '#0F0F0F', marginBottom: 4, boxSizing: 'border-box', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
            />
            <div style={{ fontSize: 11.5, color: '#9A9A9A', marginBottom: 12, lineHeight: 1.45 }}>
              If the vendor gives you their own order or confirmation number, record it here.
            </div>

            <div style={{ fontSize: 10, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>
              Est. arrival <span style={{ fontWeight: 400, textTransform: 'none' }}>(optional)</span>
            </div>
            <input
              type="date" value={moEta} onChange={e => setMoEta(e.target.value)}
              style={{ width: '100%', height: 40, border: '1px solid #E0E0E0', borderRadius: 9, padding: '0 12px', color: '#0F0F0F', marginBottom: 20, boxSizing: 'border-box' }}
            />

            {!isOffCatalog && (
              <div style={{ fontSize: 12, color: checkedCount === 0 ? '#A32D2D' : '#5C5C5C', marginBottom: 12, lineHeight: 1.4 }}>
                {checkedCount === 0
                  ? 'Select at least one item to order.'
                  : isSplitPreview
                    ? `This creates a new split request for the ${checkedCount} selected item${checkedCount !== 1 ? 's' : ''}, linked back to ${req.request_number}. The remaining ${items.length - checkedCount} item${items.length - checkedCount !== 1 ? 's stay' : ' stays'} in Needs Action for their own vendor.`
                    : `All ${items.length} item${items.length !== 1 ? 's' : ''} will be marked ordered under ${req.request_number}.`
                }
              </div>
            )}
            {liveOrderTotal > 0 && (
              <div style={{ background: willReTriggerApproval ? '#FAEEDA' : '#F5F5F3', border: `1px solid ${willReTriggerApproval ? '#EF9F27' : '#E0E0E0'}`, borderRadius: 8, padding: '8px 10px', marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: willReTriggerApproval ? '#633806' : '#0F0F0F' }}>Order total</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: willReTriggerApproval ? '#633806' : '#0F0F0F' }}>${liveOrderTotal.toFixed(2)}</span>
                </div>
                {willReTriggerApproval && (
                  <div style={{ fontSize: 11, color: '#854F0B', marginTop: 4, lineHeight: 1.45 }}>
                    {wasApproved && overApprovedBy > 0
                      ? `$${overApprovedBy.toFixed(2)} above the $${approvedAmount.toFixed(2)} ${req.approver?.full_name || 'the approver'} approved. Confirming sends this back for approval.`
                      : 'Above the approval threshold. Confirming sends this for approval before it can be ordered.'}
                  </div>
                )}
                {wasApproved && !willReTriggerApproval && (
                  <div style={{ fontSize: 11, color: '#5C5C5C', marginTop: 4, lineHeight: 1.45 }}>
                    At or below the ${approvedAmount.toFixed(2)} approved — the approval still holds.
                  </div>
                )}
              </div>
            )}
            {/* #395, Session 78n8.2 -- gate Screen 2. The end of Mark as ordered:
                the vendor's order and the requester's confirmation leave in the
                same action, so there is no second screen to forget. */}
            <SendOrderSection
              vendor={moVendor}
              branch={poBranch}
              settings={settings}
              buyer={profile}
              lines={poLines}
              poRef={moPoRef}
              requestNumber={req.request_number}
              workOrderRef={req.work_order_ref}
              requesterName={req.users?.full_name}
              mode={moSendMode} setMode={setMoSendMode}
              note={moVendorNote} setNote={setMoVendorNote}
              sendPo={moSendPo} setSendPo={setMoSendPo}
              sendConfirmation={moSendConfirmation} setSendConfirmation={setMoSendConfirmation}
              blockers={poBlockers}
              disabled={moSaving}
              isMobile={isMobile}
            />

            {/* #398. Blocked, not merely refused on click. submitMarkOrdered's
                error renders at the TOP of this sheet - measured live at 1024px
                above this button - so on a laptop the person confirms, nothing
                moves, and the sentence explaining why is a screen and a half up.
                Same rule now used by all three vendor price forms. */}
            <button onClick={onSubmit} disabled={moSaving || (!isOffCatalog && checkedCount === 0) || moUnitMissing}
              title={moUnitMissing ? 'Say what the vendor sells this by - it is what the price is per.' : undefined}
              style={{ width: '100%', padding: 14, borderRadius: 12, fontSize: 14, fontWeight: 700, border: 'none', background: (moSaving || (!isOffCatalog && checkedCount === 0) || moUnitMissing) ? '#E0E0E0' : willReTriggerApproval ? '#EF9F27' : '#1D9E75', color: (moSaving || (!isOffCatalog && checkedCount === 0) || moUnitMissing) ? '#9A9A9A' : willReTriggerApproval ? '#412402' : 'white', cursor: (moSaving || (!isOffCatalog && checkedCount === 0) || moUnitMissing) ? 'not-allowed' : 'pointer', marginBottom: 8 }}>
              {moSaving
                ? 'Saving...'
                : willReTriggerApproval
                  ? (wasApproved ? 'Confirm order — needs approval again' : 'Confirm order — needs approval')
                  : !isOffCatalog && isSplitPreview
                    ? `Confirm order — split ${checkedCount} of ${items.length} items`
                    : effectiveSendMode === 'send'
                      ? 'Mark ordered & send'
                      : effectiveSendMode === 'draft'
                        ? 'Mark ordered & open draft'
                        : 'Confirm order'
              }
            </button>
            <button onClick={onClose} style={{ width: '100%', background: 'none', border: 'none', fontSize: 13, color: '#7A7A7A', cursor: 'pointer', padding: 4 }}>Cancel</button>

            <AddVendorSheet
              visible={showAddVendor}
              onClose={() => setShowAddVendor(false)}
              onSaved={handleAddVendorSaved}
              prefillName={addVendorPrefill}
              itemId={itemId}
            />
          </div>
        )}
      </>
    </Overlay>
  );
}
